import { Card, Checkbox, Grid, Icon, IconButton, InputLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDModalAbas from "components/MDModalAbas";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { trueOrFalseEnum } from "utils/Enums";
import { verifyVariable } from "utils/Functions";
import { post } from "utils/WebService";
import { ESCURO } from "assets/coresTema";

// Tipo gravado pelo robô (ccTipoProcesso). Duplicado = mesmo processo e mesmo andamento de um já recebido no dia:
// não vai para a equipe, fica aqui para conferir com o original (mesmo processo, mesmo dia)
const TIPOS_PROCESSO = [
    { value: "ROBO/PUSH", name: "Push" },
    { value: "ROBO/PROTOCOLO", name: "Protocolo" },
    { value: "DUPLICADO", name: "Duplicado no dia" },
    { value: "DESCARTE", name: "Fora da regra" },
    { value: "SEM_PROCESSO", name: "Sem nº de processo" },
];

export default function LogRoboEmailsPushDetail({ scheduleId, closeCreateEdit, tipoAgendamento }) {

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
    });

    const [filters, setFilters] = useState({
        roboEditalStatus: null,
        processo: '',
        encontrado: null,
        tipoProcesso: null
    });

    const { roboEditalStatus } = useUser();

    useEffect(() => {
        fetchData({ ...tableState });
    }, []);


    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [refresh, setRefresh] = useState(false);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [isModal, setIsModal] = useState(false);
    const [dados, setDados] = useState(null);
    const [header, setHeader] = useState(null);
    const [json, setJson] = useState(null);
    const [filtered, setFiltered] = useState([]);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered }) => {
        setLoadingTable(true);
        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
        };

        tableState.current = updatedTableState;

        post("/robo-emails/customer/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
            idLog: scheduleId
        }).then(({ data, pages, recordsTotal }) => {
            setPages(pages);
            setData(data);
            setRecordsTotal(recordsTotal);
            setLoadingTable(false);
        }).catch(() => setLoadingTable(false));

        setRefresh(false);

    }, [refresh]);

    const userColumns = [
        {
            header: "processo",
            accessorKey: "processo",
            cell: info => <DefaultCell>{info.getValue() || "-"}</DefaultCell>,
            isSorted: true,
            cellAlign: "center",
        },
        {
            header: "tipo",
            accessorKey: "tipoProcesso",
            cell: info => <DefaultCell>{TIPOS_PROCESSO.find(({ value }) => value === info.getValue())?.name || info.getValue() || "-"}</DefaultCell>,
            isSorted: true
        },
        {
            header: "recebido em",
            accessorKey: "dataEmail",
            cell: info => <DefaultCell>{info.getValue() || "-"}</DefaultCell>,
            isSorted: true
        },
        {
            header: "status",
            accessorKey: "roboEditalStatus",
            cell: info => {
                const val = info.getValue();
                const scheduleStatusName = roboEditalStatus?.find(ht => ht.code === val);
                const label = scheduleStatusName ? scheduleStatusName.name : (val === "error" ? "Erro / Descarte" : val === "success" ? "Sucesso" : val === "processing" ? "Processando" : val || "-");
                return <DefaultCell>{tipoAgendamento == "list" ? "-" : label}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "encontrado",
            accessorKey: "encontrado",
            width: "7%",
            cell: info => (
                <Checkbox
                    checked={info.getValue()}
                    disabled
                    color="primary"
                    inputProps={{ 'aria-label': 'Ativo' }}
                />
            ),
            isSorted: false,
        },
        {
            header: "Dados",
            accessorKey: "informacao",
            cell: info => {
                const mensagem = info.getValue();
                const json = info.row.original.dados;
                const header = "Processo: " + info.row.original.processo;
                const isDisabled = !mensagem && !json;

                return (
                    <>
                        <IconButton
                            onClick={() => handleOpenModal(mensagem, json, header)}
                            disabled={isDisabled}
                            sx={{
                                opacity: isDisabled ? 0.5 : 1,
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <MDTypography variant="h4">
                                <Icon color="inherit" sx={{ marginRight: 1 }}>chat</Icon>
                            </MDTypography>
                        </IconButton>
                    </>
                );
            },
            isSorted: true
        },
    ];

    const handleOpenModal = (dados, json, header) => {
        setDados(dados);
        setJson(json);
        setHeader(header);
        setIsModal(true);
    };

    const handleCloseModal = () => {
        setIsModal(false);
        setDados(null);
        setJson(null);
        setHeader(null);
    };

    const handleCleanFilter = () => {
        setFilters({
            roboEditalStatus: null,
            processo: '',
            encontrado: null,
            tipoProcesso: null
        });

        const updatedTableState = {
            pageSize: 10,
            pageIndex: 0,
            sorting: [],
            filtered: []
        };

        tableState.current = updatedTableState;
        setFiltered(updatedTableState)
        fetchData(updatedTableState);
    };

    const handleApplyFilter = () => {
        let filtered = [];

        Object.keys(filters).forEach(filterKey => {
            if (verifyVariable(filters[filterKey]))
                if (filterKey == "roboEditalStatus") {
                    filtered.push({ id: 'status', value: filters.roboEditalStatus.id });
                } else {
                    filtered.push({ id: filterKey, value: filters[filterKey] });
                }
        })
        setFiltered({ filtered })
        fetchData({ pageSize: 10, pageIndex: 0, sorting: [], filtered });
    };

    return (
        <Grid container display="flex" justifyContent="center">
            {isModal &&
                <MDModalAbas
                    header={header}
                    dados={dados}
                    json={json}
                    show={isModal}
                    close={handleCloseModal}
                />
            }

            <Grid item xs={12} md={12}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? ESCURO.cartao : "#fafafa",
                        borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                    }}
                >
                    <MDBox p={1}>
                        <MDBox display="flex" alignItems="center" pb={2}>
                            <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1 }}>
                                <MDTypography variant="h4" color={darkMode ? "white" : "dark"}>
                                    <Icon>arrow_back</Icon>
                                </MDTypography>
                            </IconButton>
                            <MDTypography variant="h5" color={darkMode ? "white" : "dark"}>
                                Detalhes Logs Robô Emails Push
                            </MDTypography>
                        </MDBox>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Processo</InputLabel>
                                <MDInput
                                    placeholder="Digite o processo"
                                    value={filters?.processo}
                                    onChange={({ target }) => setFilters({ ...filters, processo: target.value })}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleApplyFilter();
                                        }
                                    }}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.value}
                                    getOptionLabel={(option) => option.name}
                                    name="tipo-processo"
                                    placeholder="Todos"
                                    value={TIPOS_PROCESSO.find(({ value }) => value === filters.tipoProcesso) || null}
                                    options={TIPOS_PROCESSO}
                                    onChange={(option) => setFilters({ ...filters, tipoProcesso: option ? option.value : null })}
                                />
                            </Grid>

                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={option => option.code}
                                    getOptionLabel={option => option.name}
                                    name="status-multi-select"
                                    placeholder="Todos"
                                    value={filters.roboEditalStatus}
                                    options={roboEditalStatus}
                                    onChange={selectedOptions => setFilters({ ...filters, roboEditalStatus: selectedOptions })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Encontrado?</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.value}
                                    getOptionLabel={(option) => option.name}
                                    name="encontrado"
                                    placeholder="Ambos"
                                    value={trueOrFalseEnum.find(({ value }) => value == filters.encontrado) || null}
                                    options={trueOrFalseEnum}
                                    onChange={({ value }) => {
                                        setFilters({ ...filters, encontrado: value })
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} display="flex" alignItems="center" justifyContent="end" pt={1}>
                                <MDButton size="medium" sx={{ marginRight: 2 }} variant="contained" color="info" circular={false} iconOnly={false} onClick={handleCleanFilter}>
                                    Limpar filtro
                                </MDButton>
                                <MDButton size="medium" variant="contained" color="info" circular={false} iconOnly={false} onClick={handleApplyFilter}>
                                    Aplicar filtro
                                </MDButton>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>
                <DataTable
                    loading={loadingTable}
                    columnData={userColumns}
                    data={data}
                    fetchData={fetchData}
                    totalPages={pages}
                    totalRecords={recordsTotal}
                    tableState={tableState} />
            </Grid>
        </Grid >
    );
}

